// src/pages/admin/Payouts.jsx - UPDATED WITH BANK TRANSFER SUPPORT
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  AttachMoney,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Visibility,
  Download,
  AccountBalance,
  Check,
  Close,
  MoreVert,
  AccountBalanceWallet,
  Send,
  CreditCard,
  Business,
  Person,
} from '@mui/icons-material';

const AdminPayouts = () => {
  // ============================================================
  // STATE
  // ============================================================
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Statistics State
  const [stats, setStats] = useState({
    pendingCount: 0,
    totalPendingAmount: 0,
    completedCount: 0,
    totalCompletedAmount: 0,
    processingCount: 0,
  });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog State
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Transfer Form State
  const [transferData, setTransferData] = useState({
    bankName: '',
    accountNumber: '',
    accountName: '',
    amount: '',
    reference: '',
    notes: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);

  // ============================================================
  // FETCH DATA
  // ============================================================
  const fetchPayouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getPayouts({
        page: page + 1,
        limit: rowsPerPage,
      });
      
      const data = response.data;
      setPayouts(data.payouts || []);
      setTotalCount(data.total || 0);
      
      if (data.stats) {
        setStats(data.stats);
      } else {
        // Fallback calculation
        const pendingList = data.payouts?.filter(p => p.status === 'pending') || [];
        const completedList = data.payouts?.filter(p => p.status === 'completed') || [];
        const processingList = data.payouts?.filter(p => p.status === 'processing') || [];
        setStats({
          pendingCount: pendingList.length,
          totalPendingAmount: pendingList.reduce((sum, p) => sum + parseFloat(p.amount), 0),
          completedCount: completedList.length,
          totalCompletedAmount: completedList.reduce((sum, p) => sum + parseFloat(p.amount), 0),
          processingCount: processingList.length,
        });
      }
    } catch (error) {
      setError('Failed to load payout requests. Please try again.');
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [page, rowsPerPage]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleMenuOpen = (event, payout) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayout(payout);
    // Pre-fill transfer form with vendor data
    if (payout?.Vendor) {
      setTransferData({
        bankName: payout.Vendor.bankName || '',
        accountNumber: payout.Vendor.bankAccountNumber || '',
        accountName: payout.Vendor.bankAccountName || payout.Vendor.storeName || '',
        amount: payout.amount?.toString() || '',
        reference: `PAYOUT-${payout.id}-${Date.now()}`,
        notes: `Payout to ${payout.Vendor.storeName || 'Vendor'}`,
      });
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setOpenDetailsDialog(true);
    handleMenuClose();
  };

  // ============================================================
  // ✅ OPEN TRANSFER DIALOG
  // ============================================================
  const handleOpenTransfer = () => {
    if (selectedPayout?.Vendor) {
      setTransferData({
        bankName: selectedPayout.Vendor.bankName || '',
        accountNumber: selectedPayout.Vendor.bankAccountNumber || '',
        accountName: selectedPayout.Vendor.bankAccountName || selectedPayout.Vendor.storeName || '',
        amount: selectedPayout.amount?.toString() || '',
        reference: `PAYOUT-${selectedPayout.id}-${Date.now()}`,
        notes: `Payout to ${selectedPayout.Vendor.storeName || 'Vendor'} for request #${selectedPayout.id}`,
      });
    }
    setOpenTransferDialog(true);
    handleMenuClose();
  };

  // ============================================================
  // ✅ PROCESS TRANSFER (Approve & Send Money)
  // ============================================================
  const handleProcessTransfer = async () => {
    if (!selectedPayout) return;
    
    // Validate bank details
    if (!transferData.bankName || !transferData.accountNumber || !transferData.accountName) {
      showSnackbar('Please provide complete bank details', 'error');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      // Call backend to process payout via Chapa
      const response = await adminApi.processPayoutTransfer({
        payoutId: selectedPayout.id,
        bankName: transferData.bankName,
        accountNumber: transferData.accountNumber,
        accountName: transferData.accountName,
        amount: parseFloat(transferData.amount),
        reference: transferData.reference,
        notes: transferData.notes,
      });

      if (response.success) {
        showSnackbar(`Payout of $${parseFloat(selectedPayout.amount).toFixed(2)} transferred successfully!`, 'success');
        setOpenTransferDialog(false);
        setOpenApproveDialog(false);
        fetchPayouts(); // Refresh table
      } else {
        showSnackbar(response.message || 'Transfer failed', 'error');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to process transfer.', 'error');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleApprovePayout = async () => {
    if (!selectedPayout) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      await adminApi.updatePayoutStatus(selectedPayout.id, 'processing');
      showSnackbar(`Payout request approved and marked for processing`, 'success');
      setOpenApproveDialog(false);
      fetchPayouts();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to approve payout.', 'error');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleRejectPayout = async () => {
    if (!selectedPayout) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      await adminApi.updatePayoutStatus(selectedPayout.id, 'failed');
      showSnackbar(`Payout request rejected.`, 'success');
      setOpenRejectDialog(false);
      fetchPayouts();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to reject payout.', 'error');
    } finally {
      setIsProcessing(false);
      setSelectedPayout(null);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTransferChange = (field) => (event) => {
    setTransferData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // ============================================================
  // UI HELPERS
  // ============================================================
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'processing': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'failed': return <Cancel />;
      case 'processing': return <Pending />;
      default: return <Pending />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading && payouts.length === 0 && page === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Vendor Payouts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review, approve, and process vendor withdrawal requests
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchPayouts}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              disabled={payouts.length === 0}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Pending color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.pendingCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoney color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                ${stats.totalPendingAmount?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Pending Amount
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.completedCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed Payouts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceWallet color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="info.main">
                ${stats.totalCompletedAmount?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Paid Out
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payouts Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Available Balance</TableCell>
                <TableCell>Requested Amount</TableCell>
                <TableCell>Bank Account</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No payout requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        #{payout.id}
                      </Typography>
                      {payout.transactionRef && (
                        <Typography variant="caption" color="text.secondary">
                          Ref: {payout.transactionRef}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 12 }}>
                          {payout.Vendor?.storeName?.[0]?.toUpperCase() || 'V'}
                        </Avatar>
                        <Typography variant="body2">
                          {payout.Vendor?.storeName || 'Unknown Vendor'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        ${typeof payout.Vendor?.availableBalance === 'string' 
                          ? parseFloat(payout.Vendor.availableBalance).toFixed(2) 
                          : (payout.Vendor?.availableBalance || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        ${parseFloat(payout.amount).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {payout.Vendor?.bankAccountNumber || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payout.Vendor?.bankName || ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(payout.requestedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(payout.status)}
                        label={payout.status || 'Pending'}
                        size="small"
                        color={getStatusColor(payout.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, payout)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* ============================================================ */}
      {/* CONTEXT MENU */}
      {/* ============================================================ */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleViewDetails(); }}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        
        {selectedPayout?.status === 'pending' && (
          <>
            <MenuItem onClick={() => { setOpenApproveDialog(true); handleMenuClose(); }} sx={{ color: 'success.main' }}>
              <Check fontSize="small" sx={{ mr: 1 }} /> Approve Payout
            </MenuItem>
            {/* ✅ NEW: Transfer Money Option */}
            <MenuItem onClick={handleOpenTransfer} sx={{ color: 'primary.main' }}>
              <Send fontSize="small" sx={{ mr: 1 }} /> Transfer Money
            </MenuItem>
            <MenuItem onClick={() => { setOpenRejectDialog(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
              <Close fontSize="small" sx={{ mr: 1 }} /> Reject Payout
            </MenuItem>
          </>
        )}
      </Menu>

      {/* ============================================================ */}
      {/* VIEW DETAILS DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Payout Request Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayout && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Vendor</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedPayout.Vendor?.storeName || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current Available Balance</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  ${typeof selectedPayout.Vendor?.availableBalance === 'string' 
                    ? parseFloat(selectedPayout.Vendor.availableBalance).toFixed(2) 
                    : (selectedPayout.Vendor?.availableBalance || 0).toFixed(2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Amount Requested</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  ${parseFloat(selectedPayout.amount).toFixed(2)}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Bank Details</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedPayout.Vendor?.bankName || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account Number</Typography>
                      <Typography variant="body2" fontWeight="500" sx={{ fontFamily: 'monospace' }}>
                        {selectedPayout.Vendor?.bankAccountNumber || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account Name</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {selectedPayout.Vendor?.bankAccountName || selectedPayout.Vendor?.storeName || 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip
                  icon={getStatusIcon(selectedPayout.status)}
                  label={selectedPayout.status || 'Pending'}
                  color={getStatusColor(selectedPayout.status)}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Request Date</Typography>
                <Typography variant="body2">{formatDate(selectedPayout.requestedAt)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* ✅ TRANSFER MONEY DIALOG - NEW */}
      {/* ============================================================ */}
      <Dialog
        open={openTransferDialog}
        onClose={() => setOpenTransferDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Send color="primary" />
            <Typography variant="h6">Transfer Money to Vendor</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayout && (
            <Stack spacing={3}>
              {/* Vendor Info */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    {selectedPayout.Vendor?.storeName?.[0]?.toUpperCase() || 'V'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedPayout.Vendor?.storeName || 'Unknown Vendor'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Request #{selectedPayout.id} • ${parseFloat(selectedPayout.amount).toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Bank Transfer Form */}
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'primary.main' }}>
                Bank Transfer Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank Name *"
                    value={transferData.bankName}
                    onChange={handleTransferChange('bankName')}
                    placeholder="e.g., Commercial Bank of Ethiopia"
                    required
                    InputProps={{
                      startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number *"
                    value={transferData.accountNumber}
                    onChange={handleTransferChange('accountNumber')}
                    placeholder="e.g., 1000123456789"
                    required
                    InputProps={{
                      startAdornment: <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Account Name *"
                    value={transferData.accountName}
                    onChange={handleTransferChange('accountName')}
                    placeholder="e.g., John Doe"
                    required
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    value={transferData.amount}
                    onChange={handleTransferChange('amount')}
                    disabled
                    type="number"
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference"
                    value={transferData.reference}
                    onChange={handleTransferChange('reference')}
                    placeholder="Payment reference"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={transferData.notes}
                    onChange={handleTransferChange('notes')}
                    placeholder="Additional notes about this transfer"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <Alert severity="info">
                <Typography variant="body2">
                  This transfer will be processed via Chapa payment gateway. 
                  The vendor will receive a notification once the transfer is complete.
                </Typography>
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpenTransferDialog(false)} fullWidth>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleProcessTransfer}
            disabled={isProcessing || !transferData.bankName || !transferData.accountNumber}
            fullWidth
            startIcon={<Send />}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Send Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* APPROVE DIALOG */}
      {/* ============================================================ */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} maxWidth="xs">
        <DialogTitle sx={{ color: 'success.main' }}>
          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} /> Approve Payout
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve the payout of 
            <strong> ${parseFloat(selectedPayout?.amount || 0).toFixed(2)}</strong> to 
            <strong> {selectedPayout?.Vendor?.storeName || 'Vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            This will mark the payout for processing. You can then transfer the money.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprovePayout}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Approve Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* REJECT DIALOG */}
      {/* ============================================================ */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="xs">
        <DialogTitle sx={{ color: 'error.main' }}>
          <Cancel sx={{ mr: 1, verticalAlign: 'middle' }} /> Reject Payout
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject the payout request from 
            <strong> {selectedPayout?.Vendor?.storeName || 'Vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            The request will be marked as failed and the vendor will be notified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectPayout}
            disabled={isProcessing}
          >
            {isProcessing ? <CircularProgress size={20} /> : 'Reject Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPayouts;